import { useQuery } from '@tanstack/react-query';
import { graphqlClient } from '@/lib/graphql';
import {
  GetHomepageDataDocument,
  type GetHomepageDataQuery,
} from '@/graphql/generated/graphql';

export type HomepageTopic = NonNullable<
  GetHomepageDataQuery['topics']['edges'][number]['node']
>;

export type HomepageEpisode = NonNullable<
  GetHomepageDataQuery['episodes']['edges'][number]['node']
>;

async function fetchHomepageData(): Promise<{
  topics: HomepageTopic[];
  topicsCount: number;
  episodes: HomepageEpisode[];
  episodesCount: number;
}> {
  const response = await graphqlClient.request<GetHomepageDataQuery>(
    GetHomepageDataDocument,
    { topicsFirst: 100, episodesFirst: 20 }
  );
  return {
    topics: response.topics.edges.map((edge) => edge.node),
    topicsCount: response.topics.totalCount ?? 0,
    episodes: response.episodes.edges.map((edge) => edge.node),
    episodesCount: response.episodes.totalCount ?? 0,
  };
}

export function useHomepageData() {
  const { data, isLoading, error } = useQuery({
    queryKey: ['homepage-data'],
    queryFn: fetchHomepageData,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

  return {
    topics: data?.topics ?? [],
    topicsCount: data?.topicsCount ?? 0,
    episodes: data?.episodes ?? [],
    episodesCount: data?.episodesCount ?? 0,
    isLoading,
    error,
  };
}
